// src/pages/Schedule.js
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { roleLabel, getErrorMessage } from '../utils/helpers';
import { PageWrapper } from './Dashboard';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export default function Schedule() {
  const { isSupervisor } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignModal, setAssignModal] = useState(null);
  const [overrideModal, setOverrideModal] = useState(false);
  const [assignForm, setAssignForm] = useState({ shiftId: '', effectiveFrom: new Date().toISOString().split('T')[0] });
  const [overrideForm, setOverrideForm] = useState({ staffId: '', overrideDate: new Date().toISOString().split('T')[0], shiftId: '', overrideType: 'callout', notes: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [activeView, setActiveView] = useState('grid');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [weeklyRes, shiftsRes, staffRes] = await Promise.all([
        api.get('/schedules/weekly'),
        api.get('/schedules/shifts'),
        api.get('/staff')
      ]);
      setAssignments(weeklyRes.data.assignments);
      setOverrides(weeklyRes.data.overrides);
      setShifts(shiftsRes.data);
      setStaffList(staffRes.data.filter(s => s.is_active && (s.permission_level || 1) < 3));
    } catch (err) {
      console.error('Schedule load error', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const getAssignment = (staffId, day) =>
    assignments.find(a => a.staff_id === staffId && a.day_of_week === day);

  const saveAssignment = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (assignForm.shiftId === 'remove') {
        const existing = getAssignment(assignModal.staffId, assignModal.dayOfWeek);
        if (existing) await api.delete(`/schedules/assignments/${existing.id}`);
      } else {
        await api.post('/schedules/assignments', {
          staffId: assignModal.staffId,
          shiftId: assignForm.shiftId,
          dayOfWeek: assignModal.dayOfWeek,
          effectiveFrom: assignForm.effectiveFrom
        });
      }
      setAssignModal(null);
      load();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const saveOverride = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await api.post('/schedules/overrides', {
        ...overrideForm,
        shiftId: overrideForm.overrideType === 'callout' ? undefined : overrideForm.shiftId
      });
      setOverrideModal(false);
      load();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const todayDow = new Date().getDay();

  const scheduleActions = isSupervisor ? (
    <button className="btn btn-primary btn-sm" onClick={() => { setOverrideModal(true); setError(''); }}>+ Override</button>
  ) : null;

  return (
    <PageWrapper active="schedule" title="Schedule" actions={scheduleActions}>

      <div className="tab-row">
        <div className={`tab ${activeView === 'grid' ? 'active' : ''}`} onClick={() => setActiveView('grid')}>Weekly grid</div>
        <div className={`tab ${activeView === 'overrides' ? 'active' : ''}`} onClick={() => setActiveView('overrides')}>Overrides</div>
      </div>

      {loading && <div className="spinner" />}

      {!loading && activeView === 'grid' && (
        <div>
          {/* Shift legend */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            {shifts.map(sh => (
              <div key={sh.id} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12 }}>
                <div style={{ width: 10, height: 10, borderRadius: 2, background: sh.color }} />
                <span style={{ color: '#4B5563' }}>{sh.name} ({sh.start_time.slice(0,5)}–{sh.end_time.slice(0,5)})</span>
              </div>
            ))}
          </div>

          {/* Staff grid */}
          {staffList.map(s => (
            <div key={s.id} className="card" style={{ padding: '10px 14px', marginBottom: 8 }}>
              <div style={{ fontWeight: 500, fontSize: 13, marginBottom: 8 }}>
                {s.first_name} {s.last_name}
                <span style={{ fontSize: 11, color: '#9CA3AF', marginLeft: 6 }}>{roleLabel(s.role)}</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
                {DAYS.map((day, dow) => {
                  const assignment = getAssignment(s.id, dow);
                  const shift = assignment ? shifts.find(sh => sh.id === assignment.shift_id) : null;
                  const isToday = dow === todayDow;
                  return (
                    <div
                      key={dow}
                      onClick={() => isSupervisor && setAssignModal({ staffId: s.id, dayOfWeek: dow, staffName: `${s.first_name} ${s.last_name}`, existingShift: shift })}
                      style={{
                        borderRadius: 5, padding: '4px 2px', textAlign: 'center', fontSize: 10,
                        cursor: isSupervisor ? 'pointer' : 'default',
                        background: shift ? shift.color + '22' : isToday ? '#F3F4F6' : 'transparent',
                        border: isToday ? '1px solid #1D9E75' : '1px solid transparent',
                        minHeight: 38, display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center'
                      }}
                    >
                      <div style={{ color: '#9CA3AF', marginBottom: 2 }}>{day}</div>
                      {shift ? (
                        <div style={{ fontWeight: 500, color: shift.color, fontSize: 9 }}>{shift.name}</div>
                      ) : (
                        <div style={{ color: '#D1D5DB', fontSize: 9 }}>—</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {isSupervisor && (
            <p style={{ fontSize: 12, color: '#9CA3AF', textAlign: 'center', marginTop: 8 }}>
              Tap any day cell to assign or change a shift.
            </p>
          )}
        </div>
      )}

      {!loading && activeView === 'overrides' && (
        <div>
          <button className="btn btn-primary" style={{ marginBottom: 14 }} onClick={() => { setOverrideModal(true); setError(''); }}>
            + Log callout or coverage swap
          </button>
          {overrides.length === 0 ? (
            <div className="empty-state">No overrides this week.</div>
          ) : (
            overrides.map(o => (
              <div key={o.id} className="card" style={{ borderLeft: `3px solid ${o.override_type === 'callout' ? '#E24B4A' : '#EF9F27'}`, borderRadius: '0 12px 12px 0' }}>
                <div style={{ fontWeight: 500, fontSize: 14 }}>{o.staff_name}</div>
                <div style={{ fontSize: 12, color: '#9CA3AF' }}>
                  {new Date(o.override_date).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                  {' · '}
                  <span style={{ textTransform: 'capitalize' }}>{o.override_type}</span>
                  {o.shift_name && ` → ${o.shift_name}`}
                </div>
                {o.notes && <div style={{ fontSize: 12, color: '#4B5563', marginTop: 4 }}>{o.notes}</div>}
              </div>
            ))
          )}
        </div>
      )}

      {/* Assign shift modal */}
      {assignModal && (
        <div className="modal-overlay" onClick={() => setAssignModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>Assign shift</h2>
            <p style={{ fontSize: 13, color: '#9CA3AF', marginBottom: 12 }}>
              {assignModal.staffName} · {DAY_FULL[assignModal.dayOfWeek]}
              {assignModal.existingShift && ` · Currently: ${assignModal.existingShift.name}`}
            </p>
            <form onSubmit={saveAssignment}>
              <div className="field">
                <label>Shift</label>
                <select required value={assignForm.shiftId} onChange={e => setAssignForm(f => ({ ...f, shiftId: e.target.value }))}>
                  <option value="">— Select shift —</option>
                  {shifts.map(sh => (
                    <option key={sh.id} value={sh.id}>{sh.name} ({sh.start_time.slice(0,5)}–{sh.end_time.slice(0,5)})</option>
                  ))}
                  {assignModal.existingShift && <option value="remove">Remove assignment (day off)</option>}
                </select>
              </div>
              <div className="field">
                <label>Effective from</label>
                <input type="date" value={assignForm.effectiveFrom} onChange={e => setAssignForm(f => ({ ...f, effectiveFrom: e.target.value }))} required />
              </div>
              {error && <div className="alert-bar danger" style={{ marginBottom: 8 }}>{error}</div>}
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
                <button className="btn btn-secondary" type="button" onClick={() => setAssignModal(null)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Override modal */}
      {overrideModal && (
        <div className="modal-overlay" onClick={() => setOverrideModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>Log schedule override</h2>
            <form onSubmit={saveOverride}>
              <div className="field">
                <label>Staff member</label>
                <select required value={overrideForm.staffId} onChange={e => setOverrideForm(f => ({ ...f, staffId: e.target.value }))}>
                  <option value="">— Select staff —</option>
                  {staffList.map(s => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
                </select>
              </div>
              <div className="field">
                <label>Date</label>
                <input type="date" required value={overrideForm.overrideDate} onChange={e => setOverrideForm(f => ({ ...f, overrideDate: e.target.value }))} />
              </div>
              <div className="field">
                <label>Type</label>
                <select value={overrideForm.overrideType} onChange={e => setOverrideForm(f => ({ ...f, overrideType: e.target.value }))}>
                  <option value="callout">Call-out (not coming in)</option>
                  <option value="swap">Coverage swap (different shift)</option>
                  <option value="extra">Extra shift (additional day)</option>
                </select>
              </div>
              {overrideForm.overrideType !== 'callout' && (
                <div className="field">
                  <label>Shift</label>
                  <select value={overrideForm.shiftId} onChange={e => setOverrideForm(f => ({ ...f, shiftId: e.target.value }))}>
                    <option value="">— Select shift —</option>
                    {shifts.map(sh => <option key={sh.id} value={sh.id}>{sh.name} ({sh.start_time.slice(0,5)}–{sh.end_time.slice(0,5)})</option>)}
                  </select>
                </div>
              )}
              <div className="field">
                <label>Notes (optional)</label>
                <textarea value={overrideForm.notes} onChange={e => setOverrideForm(f => ({ ...f, notes: e.target.value }))} placeholder="e.g. Covering for Sarah Kim" />
              </div>
              {error && <div className="alert-bar danger" style={{ marginBottom: 8 }}>{error}</div>}
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
                <button className="btn btn-secondary" type="button" onClick={() => setOverrideModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

    </PageWrapper>
  );
}
